import React, { useEffect, useState, useRef } from 'react';
import {
  FolderPlus, FolderOpen, Trash2, Pencil, Upload, FileText,
  Download, GripVertical, ChevronRight, Folder,
  Lock, Shield, X, UserPlus, Search, Check, ChevronDown, RefreshCw
} from 'lucide-react';
import { useApi } from './DashboardLayout';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileColor(name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  const colors = { pdf: '#EF4444', doc: '#3B82F6', docx: '#3B82F6', xls: '#10B981', xlsx: '#10B981', ppt: '#F59E0B', pptx: '#F59E0B', zip: '#8B5CF6', jpg: '#EC4899', png: '#EC4899', txt: '#6B7280' };
  return colors[ext] || '#6B7280';
}

function fileExtLabel(name) {
  return (name?.split('.').pop()?.toUpperCase()) || '';
}

/* ─── Sortable file row (admin) ─── */
function SortableFileRow({ f, isAdmin, onDownload, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.path });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <tr ref={setNodeRef} style={style} data-testid={`file-row-${f.name}`}>
      {isAdmin && (
        <td style={{ width: 28, padding: '8px 4px 8px 16px' }}>
          <div {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--text-muted)', opacity: 0.4, display: 'flex', alignItems: 'center' }}>
            <GripVertical size={14} />
          </div>
        </td>
      )}
      <td style={{ padding: '10px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, fontSize: 9, fontWeight: 800,
            color: '#fff', background: fileColor(f.name), letterSpacing: '0.03em',
            marginTop: 1,
          }}>
            {fileExtLabel(f.name)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 500 }}
              className="dl-file-name" onClick={() => onDownload(f)}>
              <span style={{ fontSize: 13 }}>{f.display_name || f.name}</span>
            </div>
            {f.description && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: 2 }}
                data-testid={`file-desc-${f.name}`}>
                {f.description}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right' }}>
        {formatSize(f.size)}
      </td>
      {isAdmin && (
        <td style={{ padding: '10px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button className="btn-icon" onClick={() => onDownload(f)} title="Herunterladen" style={{ color: 'var(--primary)' }}>
            <Download size={15} />
          </button>
          <button className="btn-icon" onClick={() => onDelete(f)} title="Löschen" style={{ color: '#EF4444', marginLeft: 2 }}>
            <Trash2 size={15} />
          </button>
        </td>
      )}
    </tr>
  );
}

/* ─── Sortable category card (for drag reorder) ─── */
function SortableCatWrapper({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}


export default function DownloadsPage({ userType }) {
  const { apiFetch } = useApi();
  const isAdmin = userType === 1 || userType === 2;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Admin: new category
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);

  // Admin: rename
  const [renamingCat, setRenamingCat] = useState(null);
  const [renameVal, setRenameVal] = useState('');

  // Admin: file upload
  const fileInputRefs = useRef({});
  const [uploadingCat, setUploadingCat] = useState(null);

  // Admin: access control
  const [accessFolder, setAccessFolder] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [providerSearch, setProviderSearch] = useState('');
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Admin: archive / Papierkorb
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [restoringItem, setRestoringItem] = useState(null);

  // Admin: GBM protected folders
  const [gbmProtectedPaths, setGbmProtectedPaths] = useState(new Set());

  useEffect(() => { loadAll(); loadGbmProtected(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/downloads/categories-with-files');
      if (r?.ok) {
        const d = await r.json();
        setData(d);
      }
    } catch { /* */ }
    setLoading(false);
  };

  const loadGbmProtected = async () => {
    if (!isAdmin) return;
    try {
      const r = await apiFetch('/api/gbm/protected-folders');
      if (r?.ok) {
        const arr = await r.json();
        setGbmProtectedPaths(new Set(arr));
      }
    } catch { /* */ }
  };

  const toggleCat = (path) => {
    setExpandedCat(prev => prev === path ? null : path);
  };

  // ── Admin: Archive functions ──
  const loadTrash = async () => {
    setLoadingTrash(true);
    try {
      const r = await apiFetch('/api/downloads/archive');
      if (r?.ok) setTrashItems(await r.json());
    } catch { /* */ }
    setLoadingTrash(false);
  };

  const toggleTrash = () => {
    if (!showTrash) loadTrash();
    setShowTrash(prev => !prev);
  };

  const restoreItem = async (item) => {
    setRestoringItem(item.archive_path);
    const r = await apiFetch('/api/downloads/archive/restore', {
      method: 'POST', body: JSON.stringify({ archive_path: item.archive_path })
    });
    if (r?.ok) {
      setTrashItems(prev => prev.filter(i => i.archive_path !== item.archive_path));
      await loadAll();
    } else {
      const err = await r?.json().catch(() => null);
      alert(err?.detail || 'Wiederherstellung fehlgeschlagen');
    }
    setRestoringItem(null);
  };

  const permanentlyDelete = async (item) => {
    if (!window.confirm(`"${item.original_name}" endgültig und unwiderruflich löschen?`)) return;
    await apiFetch('/api/downloads/archive', {
      method: 'DELETE', body: JSON.stringify({ archive_path: item.archive_path })
    });
    setTrashItems(prev => prev.filter(i => i.archive_path !== item.archive_path));
  };

  const emptyTrash = async () => {
    if (!window.confirm('Gesamten Papierkorb endgültig leeren? Dies kann nicht rückgängig gemacht werden.')) return;
    await apiFetch('/api/downloads/archive/empty', { method: 'DELETE' });
    setTrashItems([]);
  };

  const downloadFile = async (file) => {
    setDownloading(file.path);
    try {
      const r = await apiFetch(`/api/downloads/file/link?path=${encodeURIComponent(file.path)}`);
      const d = await r?.json();
      if (d?.url) window.open(d.url, '_blank');
    } catch { /* */ }
    setDownloading(null);
  };

  // ── Admin: Create category ──
  const createCategory = async () => {
    if (!newCatName.trim()) return;
    setCreatingCat(true);
    const r = await apiFetch('/api/downloads/categories', {
      method: 'POST', body: JSON.stringify({ name: newCatName.trim() })
    });
    if (r?.ok) { setNewCatName(''); setShowNewCat(false); await loadAll(); }
    else { const err = await r?.json().catch(() => null); alert(err?.detail || 'Fehler'); }
    setCreatingCat(false);
  };

  // ── Admin: Rename category ──
  const doRenameCategory = async (cat) => {
    if (!renameVal.trim()) return;
    const r = await apiFetch(`/api/downloads/categories/${encodeURIComponent(cat.path)}`, {
      method: 'PUT', body: JSON.stringify({ name: renameVal.trim(), old_path: cat.path })
    });
    if (r?.ok) { setRenamingCat(null); setRenameVal(''); await loadAll(); }
    else { const err = await r?.json().catch(() => null); alert(err?.detail || 'Fehler'); }
  };

  // ── Admin: Delete category ──
  const deleteCategory = async (cat) => {
    if (!window.confirm(`"${cat.name}" in den Papierkorb verschieben?`)) return;
    await apiFetch('/api/downloads/categories', { method: 'DELETE', body: JSON.stringify({ path: cat.path }) });
    if (expandedCat === cat.path) setExpandedCat(null);
    await loadAll();
  };

  // ── Admin: Upload file ──
  const uploadFileToCat = async (catPath, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCat(catPath);
    const formData = new FormData();
    formData.append('file', file);
    await apiFetch(`/api/downloads/upload?path=${encodeURIComponent(catPath)}`, { method: 'POST', body: formData });
    setUploadingCat(null);
    if (fileInputRefs.current[catPath]) fileInputRefs.current[catPath].value = '';
    await loadAll();
  };

  // ── Admin: Delete file ──
  const deleteFile = async (file) => {
    if (!window.confirm(`"${file.name}" in den Papierkorb verschieben?`)) return;
    await apiFetch('/api/downloads/file', { method: 'DELETE', body: JSON.stringify({ path: file.path }) });
    await loadAll();
  };

  // ── Admin: Category DnD sort ──
  const handleCatDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !data) return;
    const cats = data.categories;
    const oldIndex = cats.findIndex(c => c.path === active.id);
    const newIndex = cats.findIndex(c => c.path === over.id);
    const newOrder = arrayMove(cats, oldIndex, newIndex);
    setData(prev => ({ ...prev, categories: newOrder }));
    await apiFetch('/api/downloads/sort-order/categories', {
      method: 'PUT', body: JSON.stringify({ order: newOrder.map(c => c.path) })
    });
  };

  // ── Admin: File DnD sort within category ──
  const handleFileDragEnd = async (catPath, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !data) return;
    const cat = data.categories.find(c => c.path === catPath);
    if (!cat) return;
    const oldIndex = cat.files.findIndex(f => f.path === active.id);
    const newIndex = cat.files.findIndex(f => f.path === over.id);
    const newFiles = arrayMove(cat.files, oldIndex, newIndex);
    setData(prev => ({
      ...prev,
      categories: prev.categories.map(c => c.path === catPath ? { ...c, files: newFiles } : c)
    }));
    await apiFetch('/api/downloads/sort-order/files', {
      method: 'PUT', body: JSON.stringify({ category_path: catPath, order: newFiles.map(f => f.path) })
    });
  };

  // ── Admin: Access control ──
  const openAccessDialog = async (cat) => {
    setAccessFolder(cat); setLoadingAccess(true); setProviderSearch('');
    const [accessRes, provRes] = await Promise.all([
      apiFetch(`/api/downloads/folder-access?folder_path=${encodeURIComponent(cat.path)}`),
      allProviders.length > 0 ? null : apiFetch('/api/mail/recipients/providers'),
    ]);
    if (accessRes?.ok) setAccessList(await accessRes.json());
    if (provRes?.ok) setAllProviders(await provRes.json());
    setLoadingAccess(false);
  };

  const toggleRestriction = async (cat, restricted) => {
    await apiFetch('/api/downloads/folder-restrictions', {
      method: 'POST', body: JSON.stringify({ folder_path: cat.path, is_restricted: restricted })
    });
    if (!restricted) { setAccessList([]); setAccessFolder(prev => prev ? { ...prev, is_restricted: false } : null); }
    else { setAccessFolder(prev => prev ? { ...prev, is_restricted: true } : null); }
    await loadAll();
  };

  const grantAccess = async (providerId) => {
    if (!accessFolder) return;
    const r = await apiFetch('/api/downloads/folder-access', {
      method: 'POST', body: JSON.stringify({ folder_path: accessFolder.path, provider_id: providerId })
    });
    if (r?.ok) {
      const res = await apiFetch(`/api/downloads/folder-access?folder_path=${encodeURIComponent(accessFolder.path)}`);
      if (res?.ok) setAccessList(await res.json());
    }
  };

  const revokeAccess = async (providerId) => {
    if (!accessFolder) return;
    await apiFetch('/api/downloads/folder-access', {
      method: 'DELETE', body: JSON.stringify({ folder_path: accessFolder.path, provider_id: providerId })
    });
    setAccessList(prev => prev.filter(a => a.provider_id !== providerId));
  };

  const accessProviderIds = new Set(accessList.map(a => a.provider_id));
  const filteredProviders = allProviders.filter(p =>
    !accessProviderIds.has(p.id) &&
    (providerSearch === '' ||
      (p.verein || '').toLowerCase().includes(providerSearch.toLowerCase()) ||
      (p.email || '').toLowerCase().includes(providerSearch.toLowerCase()) ||
      String(p.club_id || '').includes(providerSearch))
  );

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;
  if (!data) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Downloadbereich nicht verfügbar.</div>;

  const categories = data.categories || [];

  return (
    <div data-testid="downloads-page">
      <style>{`
        .dl-cat-card {
          border: 1px solid var(--border); border-radius: 10px; overflow: hidden;
          margin-bottom: 8px; background: var(--bg-primary, #fff); transition: box-shadow 0.15s;
        }
        .dl-cat-card:hover { box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
        .dl-cat-header {
          display: flex; align-items: center; padding: 14px 20px; cursor: pointer;
          transition: background 0.15s; gap: 10px; user-select: none;
        }
        .dl-cat-header:hover { background: var(--bg-secondary, #f9fafb); }
        .dl-cat-body { border-top: 1px solid var(--border); }
        .dl-file-table { width: 100%; border-collapse: collapse; }
        .dl-file-table thead th {
          text-align: left; padding: 8px 16px; font-size: 11px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted);
          background: var(--bg-secondary, #f5f5f5); border-bottom: 1px solid var(--border);
        }
        .dl-file-table thead th:last-child { text-align: right; }
        .dl-file-table tbody tr { transition: background 0.1s; }
        .dl-file-table tbody tr:hover { background: var(--bg-secondary, #fafafa); }
        .dl-file-table tbody td { vertical-align: middle; }
        .dl-file-table tbody tr:last-child td { border-bottom: none; }
        .dl-file-name:hover { text-decoration: underline; }
        .dl-chevron { transition: transform 0.2s; color: var(--text-muted); flex-shrink: 0; }
        .dl-chevron.open { transform: rotate(90deg); }
        .dl-cat-count {
          font-size: 11px; color: var(--text-muted); background: var(--bg-secondary, #f0f0f0);
          padding: 2px 8px; border-radius: 10px; font-weight: 600; flex-shrink: 0;
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Downloads</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>Dokumente und Unterlagen</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button className="btn btn-primary" style={{ fontSize: 13 }}
              onClick={() => setShowNewCat(true)} data-testid="new-category-btn">
              <FolderPlus size={14} /> Neue Kategorie
            </button>
          )}
        </div>
      </div>

      {/* Intro text */}
      {data.intro && (
        <div className="of-card" style={{ marginBottom: 16 }} data-testid="downloads-intro-text">
          <div className="tiptap-content" dangerouslySetInnerHTML={{ __html: data.intro }}
            style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary)' }} />
        </div>
      )}

      {/* Admin: New category form */}
      {showNewCat && (
        <div className="of-card" style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div style={{ flex: 1, maxWidth: 360 }}>
            <label className="of-label">Neue Kategorie</label>
            <input className="of-input" value={newCatName} onChange={e => setNewCatName(e.target.value)} autoFocus
              placeholder="z.B. Formulare" onKeyDown={e => e.key === 'Enter' && createCategory()}
              data-testid="new-category-input" />
          </div>
          <button className="btn btn-primary btn-sm" onClick={createCategory}
            disabled={!newCatName.trim() || creatingCat} data-testid="create-category-btn"
            style={{ padding: '7px 16px' }}>{creatingCat ? 'Erstellen...' : 'Erstellen'}</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewCat(false); setNewCatName(''); }}
            style={{ padding: '7px 12px' }}>Abbrechen</button>
        </div>
      )}

      {/* Categories accordion */}
      {categories.length === 0 ? (
        <div className="of-card" style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <Folder size={36} style={{ opacity: 0.2, marginBottom: 8 }} />
          <p style={{ margin: 0, fontSize: 13 }}>Noch keine Kategorien vorhanden.</p>
        </div>
      ) : isAdmin ? (
        /* Admin: sortable categories */
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
          <SortableContext items={categories.map(c => c.path)} strategy={verticalListSortingStrategy}>
            {categories.map(cat => (
              <SortableCatWrapper key={cat.path} id={cat.path}>
                {({ dragHandleProps }) => (
                  <CategoryCard
                    cat={cat} isAdmin={isAdmin} isOpen={expandedCat === cat.path}
                    onToggle={() => toggleCat(cat.path)}
                    onDownload={downloadFile} downloading={downloading}
                    dragHandleProps={dragHandleProps}
                    onRename={(c) => { setRenamingCat(c.path); setRenameVal(c.name); }}
                    onDelete={deleteCategory}
                    onManageAccess={openAccessDialog}
                    isRenaming={renamingCat === cat.path}
                    renameVal={renameVal} setRenameVal={setRenameVal}
                    doRenameCategory={doRenameCategory}
                    cancelRename={() => setRenamingCat(null)}
                    fileInputRefs={fileInputRefs}
                    uploadFileToCat={uploadFileToCat}
                    uploadingCat={uploadingCat}
                    deleteFile={deleteFile}
                    sensors={sensors}
                    handleFileDragEnd={handleFileDragEnd}
                    isGbmProtected={gbmProtectedPaths.has(cat.path)}
                  />
                )}
              </SortableCatWrapper>
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        /* Member: plain categories */
        categories.map(cat => (
          <CategoryCard
            key={cat.path} cat={cat} isAdmin={false}
            isOpen={expandedCat === cat.path}
            onToggle={() => toggleCat(cat.path)}
            onDownload={downloadFile} downloading={downloading}
          />
        ))
      )}

      {/* ═══ Admin: Papierkorb ═══ */}
      {isAdmin && (
        <div style={{ marginTop: 24 }} data-testid="trash-section">
          <div
            onClick={toggleTrash}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
              cursor: 'pointer', borderRadius: 10, border: '1px solid var(--border)',
              background: showTrash ? 'var(--bg-secondary, #f9fafb)' : 'transparent',
              transition: 'background 0.15s', userSelect: 'none',
            }}
            data-testid="toggle-trash-btn"
          >
            <ChevronRight size={16} className={`dl-chevron ${showTrash ? 'open' : ''}`} />
            <Trash2 size={16} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Papierkorb</span>
            {trashItems.length > 0 && (
              <span style={{
                fontSize: 11, background: '#FEE2E2', color: '#991B1B',
                padding: '1px 8px', borderRadius: 10, fontWeight: 600,
              }}>{trashItems.length}</span>
            )}
          </div>

          {showTrash && (
            <div style={{
              border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 10px 10px',
              overflow: 'hidden',
            }}>
              {loadingTrash ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Papierkorb wird geladen...
                </div>
              ) : trashItems.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Der Papierkorb ist leer.
                </div>
              ) : (
                <>
                  {/* Header with empty button */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 16px', background: 'var(--bg-secondary, #f9fafb)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                      {trashItems.length} {trashItems.length === 1 ? 'Element' : 'Elemente'}
                    </span>
                    <button className="btn btn-secondary btn-sm" onClick={emptyTrash}
                      data-testid="empty-trash-btn"
                      style={{ fontSize: 11, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={12} /> Papierkorb leeren
                    </button>
                  </div>

                  {/* Items */}
                  {trashItems.map(item => (
                    <div key={item.archive_path} style={{
                      display: 'flex', alignItems: 'center', padding: '10px 16px',
                      borderBottom: '1px solid var(--border)', gap: 12, fontSize: 13,
                    }} data-testid={`trash-item-${item.original_name}`}>
                      {/* Icon */}
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0,
                        background: item.item_type === 'category' ? '#FEF3C7' : fileColor(item.original_name) + '15',
                      }}>
                        {item.item_type === 'category'
                          ? <FolderOpen size={16} style={{ color: '#D97706' }} />
                          : <FileText size={16} style={{ color: fileColor(item.original_name) }} />
                        }
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.original_name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span>{item.item_type === 'category' ? 'Kategorie' : 'Datei'}</span>
                          {item.size && <span>{formatSize(item.size)}</span>}
                          <span>Gelöscht: {new Date(item.deleted_at).toLocaleDateString('de-DE', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}</span>
                          {item.deleted_by && <span>von {item.deleted_by}</span>}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => restoreItem(item)}
                          disabled={restoringItem === item.archive_path}
                          data-testid={`restore-${item.original_name}`}
                          style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px' }}>
                          <RefreshCw size={12} /> {restoringItem === item.archive_path ? 'Wird...' : 'Wiederherstellen'}
                        </button>
                        <button className="btn-icon" onClick={() => permanentlyDelete(item)}
                          title="Endgültig löschen" style={{ color: '#991B1B' }}
                          data-testid={`perm-delete-${item.original_name}`}>
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Access Control Modal */}
      {accessFolder && (
        <AccessControlModal
          accessFolder={accessFolder} setAccessFolder={setAccessFolder}
          accessList={accessList} loadingAccess={loadingAccess}
          toggleRestriction={toggleRestriction}
          grantAccess={grantAccess} revokeAccess={revokeAccess}
          filteredProviders={filteredProviders}
          providerSearch={providerSearch} setProviderSearch={setProviderSearch}
        />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════
   CategoryCard — shared by admin + member
   ═══════════════════════════════════════════ */
function CategoryCard({
  cat, isAdmin, isOpen, onToggle, onDownload, downloading,
  dragHandleProps, onRename, onDelete, onManageAccess,
  isRenaming, renameVal, setRenameVal, doRenameCategory, cancelRename,
  fileInputRefs, uploadFileToCat, uploadingCat, deleteFile,
  sensors, handleFileDragEnd,
  isGbmProtected,
}) {
  return (
    <div className="dl-cat-card" data-testid={`cat-card-${cat.name}`}>
      {/* Header */}
      <div className="dl-cat-header">
        {/* Admin: drag handle */}
        {isAdmin && dragHandleProps && (
          <div {...dragHandleProps} style={{ cursor: 'grab', color: 'var(--text-muted)', opacity: 0.4, display: 'flex', alignItems: 'center', flexShrink: 0 }}
            onClick={e => e.stopPropagation()}>
            <GripVertical size={16} />
          </div>
        )}

        {/* Clickable area */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}
          onClick={onToggle}>
          <ChevronRight size={16} className={`dl-chevron ${isOpen ? 'open' : ''}`} />
          <FolderOpen size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />

          {isRenaming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }} onClick={e => e.stopPropagation()}>
              <input className="of-input" value={renameVal} onChange={e => setRenameVal(e.target.value)}
                autoFocus style={{ fontSize: 13, padding: '4px 8px', maxWidth: 280 }}
                onKeyDown={e => { if (e.key === 'Enter') doRenameCategory(cat); if (e.key === 'Escape') cancelRename(); }}
                data-testid="rename-category-input" />
              <button className="btn btn-primary btn-sm" onClick={() => doRenameCategory(cat)}
                style={{ padding: '3px 10px', fontSize: 11 }}>OK</button>
              <button className="btn btn-secondary btn-sm" onClick={cancelRename}
                style={{ padding: '3px 10px', fontSize: 11 }}><X size={12} /></button>
            </div>
          ) : (
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, wordBreak: 'break-word' }}>{cat.name}</span>
          )}
        </div>

        {/* Badge + admin buttons */}
        {isAdmin && isGbmProtected && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: '#EFF6FF', color: '#1D4ED8', flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }} data-testid={`gbm-badge-${cat.name}`}>
            <Shield size={10} /> GBM
          </span>
        )}
        <span className="dl-cat-count">{cat.files.length} {cat.files.length === 1 ? 'Datei' : 'Dateien'}</span>

        {isAdmin && !isRenaming && (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button className="btn-icon" title="Umbenennen" onClick={() => onRename(cat)}
              data-testid={`rename-cat-${cat.name}`}>
              <Pencil size={13} />
            </button>
            <button className="btn-icon" title="Löschen" style={{ color: '#EF4444' }}
              onClick={() => onDelete(cat)} data-testid={`delete-cat-${cat.name}`}>
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Body (expanded) */}
      {isOpen && (
        <div className="dl-cat-body" data-testid={`cat-body-${cat.name}`}>
          {/* Description */}
          {cat.description && (
            <div style={{ padding: '12px 20px', borderBottom: cat.files.length > 0 || isAdmin ? '1px solid var(--border)' : 'none' }}>
              <div className="tiptap-content" dangerouslySetInnerHTML={{ __html: cat.description }}
                style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary)' }} />
            </div>
          )}

          {/* Admin: upload bar */}
          {isAdmin && (
            <div style={{
              padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              borderBottom: cat.files.length > 0 ? '1px solid var(--border)' : 'none',
            }}>
              <input type="file"
                ref={el => { if (fileInputRefs) fileInputRefs.current[cat.path] = el; }}
                onChange={e => uploadFileToCat(cat.path, e)}
                style={{ display: 'none' }}
                data-testid={`upload-input-${cat.name}`} />
              <button className="btn btn-primary btn-sm"
                onClick={() => fileInputRefs?.current[cat.path]?.click()}
                disabled={uploadingCat === cat.path}
                data-testid={`upload-btn-${cat.name}`}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                <Upload size={13} /> {uploadingCat === cat.path ? 'Hochladen...' : 'Datei hochladen'}
              </button>
            </div>
          )}

          {/* Files table */}
          {cat.files.length === 0 ? (
            <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Keine Dateien in dieser Kategorie.
            </div>
          ) : isAdmin && sensors ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
              onDragEnd={(e) => handleFileDragEnd(cat.path, e)}>
              <SortableContext items={cat.files.map(f => f.path)} strategy={verticalListSortingStrategy}>
                <table className="dl-file-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}></th>
                      <th>Name</th>
                      <th style={{ textAlign: 'right' }}>Größe</th>
                      <th style={{ textAlign: 'right', width: 80 }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.files.map(f => (
                      <SortableFileRow key={f.path} f={f} isAdmin={true}
                        onDownload={onDownload} onDelete={deleteFile} />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          ) : (
            <table className="dl-file-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ textAlign: 'right' }}>Größe</th>
                </tr>
              </thead>
              <tbody>
                {cat.files.map(f => (
                  <tr key={f.path} data-testid={`file-row-${f.name}`}>
                    <td style={{ padding: '10px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0, fontSize: 9, fontWeight: 800,
                          color: '#fff', background: fileColor(f.name), letterSpacing: '0.03em',
                          marginTop: 1,
                        }}>
                          {fileExtLabel(f.name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}
                            className="dl-file-name" onClick={() => onDownload(f)}>
                            <span style={{ fontSize: 13 }}>{f.display_name || f.name}</span>
                            {downloading === f.path && (
                              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>Laden...</span>
                            )}
                          </div>
                          {f.description && (
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: 2 }}
                              data-testid={`file-desc-${f.name}`}>
                              {f.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 20px', color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {formatSize(f.size)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════
   Access Control Modal
   ═══════════════════════════════════════════ */
function AccessControlModal({
  accessFolder, setAccessFolder, accessList, loadingAccess,
  toggleRestriction, grantAccess, revokeAccess,
  filteredProviders, providerSearch, setProviderSearch,
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }} onClick={() => setAccessFolder(null)} data-testid="access-modal-overlay">
      <div style={{
        background: 'var(--bg-primary, #fff)', borderRadius: 12, width: '100%', maxWidth: 520,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }} onClick={e => e.stopPropagation()} data-testid="access-modal">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Zugriff verwalten</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{accessFolder.name}</p>
          </div>
          <button className="btn-icon" onClick={() => setAccessFolder(null)} data-testid="close-access-modal"><X size={18} /></button>
        </div>
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {/* Restriction toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderRadius: 8,
            background: accessFolder.is_restricted ? '#FEF2F2' : 'var(--bg-secondary, #f9fafb)',
            marginBottom: 16, border: `1px solid ${accessFolder.is_restricted ? '#FECACA' : 'var(--border)'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock size={16} style={{ color: accessFolder.is_restricted ? '#DC2626' : 'var(--text-muted)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Eingeschränkter Zugriff</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Nur freigeschaltete Anbieter sehen diesen Ordner</div>
              </div>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!accessFolder.is_restricted}
                onChange={e => toggleRestriction(accessFolder, e.target.checked)}
                style={{ display: 'none' }} data-testid="restriction-toggle" />
              <span style={{
                position: 'absolute', inset: 0, borderRadius: 11, transition: '0.2s',
                background: accessFolder.is_restricted ? '#DC2626' : '#D1D5DB',
              }} />
              <span style={{
                position: 'absolute', top: 2, left: accessFolder.is_restricted ? 20 : 2,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: '0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </label>
          </div>

          {accessFolder.is_restricted && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
                  Freigeschaltete Anbieter ({accessList.length})
                </div>
                {loadingAccess ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 8 }}>Laden...</div>
                ) : accessList.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>Noch keine Anbieter freigeschaltet</div>
                ) : (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                    {accessList.map(a => (
                      <div key={a.provider_id} style={{
                        display: 'flex', alignItems: 'center', padding: '8px 12px',
                        borderBottom: '1px solid var(--border)', gap: 8, fontSize: 12,
                      }} data-testid={`access-provider-${a.provider_id}`}>
                        <Check size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{a.verein || 'Unbekannt'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.email}</div>
                        </div>
                        <button className="btn-icon" onClick={() => revokeAccess(a.provider_id)}
                          title="Zugriff entziehen" style={{ color: '#EF4444' }}
                          data-testid={`revoke-${a.provider_id}`}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Anbieter freischalten</div>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input className="of-input" value={providerSearch} onChange={e => setProviderSearch(e.target.value)}
                    placeholder="Verein, E-Mail oder Club-ID suchen..."
                    style={{ paddingLeft: 32, fontSize: 12 }} data-testid="provider-search" />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                  {filteredProviders.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                      {providerSearch ? 'Kein Anbieter gefunden' : 'Alle Anbieter bereits freigeschaltet'}
                    </div>
                  ) : (
                    filteredProviders.slice(0, 50).map(p => (
                      <div key={p.id} style={{
                        display: 'flex', alignItems: 'center', padding: '6px 12px',
                        borderBottom: '1px solid var(--border)', gap: 8, cursor: 'pointer',
                      }}
                        onClick={() => grantAccess(p.id)}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f9fafb)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        data-testid={`grant-${p.id}`}>
                        <UserPlus size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 12 }}>{p.verein || '–'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.email}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
